import React, { useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { Helmet } from "react-helmet-async"
import {
  fetchCollections,
  selectCollectionsByContext,
  selectCollectionsByIds,
} from "./slice"
import { parseCollectionFilters } from "./filters"
import { updateSearchParamsWithFilters } from "../filters/filters"
import Layout from "../../ui/Layout"
import NavMain from "../../app/NavMain"
import ScrollablePage from "../../ui/ScrollablePage"
import Paper from "../../ui/Paper"
import CollectionsTable from "./CollectionsTable"
import ErrorMsg from "../../ui/ErrorMsg"
import LoadingSpinner, { Backdrop } from "../../ui/LoadingSpinner"
import { Header, Overview } from "../../ui/PageHeader"
import LoadingSpinnerInline from "../../ui/LoadingSpinnerInline"

const PAGE_SIZE = 50 // Default initial and subsequent page size

/* CollectionsList renders a page containing a filter- and sort-able table
 * of all collections matching the criteria in the current URL.  */
const CollectionsList = () => {
  const dispatch = useDispatch()

  // we get any matching observations for this RSO
  const collectionMatches = useSelector((state) =>
    selectCollectionsByContext(state, "root")
  )
  // We get the full objects from the ids that match
  const collections = useSelector((state) =>
    selectCollectionsByIds(state, collectionMatches?.ids || [])
  )

  // Parse parameters into filter object (which drives filters UI state)
  // Note: We are using the URL as the "state" here, and updates
  // perform a history replacement (so as not to mess up "back" button
  // behaviour too much - though we may want to apply a heuristic like
  // small changes replace history, big changes push history)
  const location = useLocation()
  const navigate = useNavigate()
  let filter = {}
  let urlParams = {}

  urlParams = new URLSearchParams(location.search)
  filter = parseCollectionFilters(urlParams)
  updateSearchParamsWithFilters(urlParams, filter)
  useEffect(() => {
    dispatch(
      fetchCollections({
        filter,
        page: { offset: 0, limit: PAGE_SIZE },
        context: ["root"],
      })
    )
  }, [urlParams.toString()])

  // How we apply a filter update. Filter state is entirely in the URL query parameters
  const setFilter = (filter = {}) => {
    const updUrlParams = updateSearchParamsWithFilters(urlParams, filter)
    navigate({
      pathname: location.pathname,
      search: `?${updUrlParams}`,
    })
  }

  // How many more observations are available to load?
  const loadableCount = Math.min(
    PAGE_SIZE,
    collections &&
      collections.length &&
      collectionMatches &&
      collectionMatches.page &&
      collectionMatches.page.total_count
      ? collectionMatches.page.total_count - collections.length
      : 50
  )

  // Event handler for 'Show xxx more' button that was clicked
  const handleLoadMore = () => {
    const lastIndex = collections && collections.length

    const page = {
      offset: lastIndex,
      limit: loadableCount,
    }
    dispatch(fetchCollections({ filter, page, context: ["root"] }))
  }

  return (
    <Layout>
      <NavMain key="nav-left" />
      <Helmet>
        <title>Collections - Astrosite</title>
      </Helmet>
      <ScrollablePage>
        {collections.length === 0 &&
          collectionMatches?._loadStatus === "loading" && (
            <Backdrop>
              <LoadingSpinner />
            </Backdrop>
          )}
        <Header>
          <Overview>
            <h1>Collections</h1>
            <p>
              {collections.length > 0 && (
                <span>
                  {collectionMatches?._loadStatus === "loading" && (
                    <LoadingSpinnerInline />
                  )}
                  Showing <strong>{collectionMatches?.page?.end_index}</strong>
                  {collectionMatches?.page?.total_count && (
                    <span>
                      {" "}
                      of{" "}
                      <strong className="count total_count">
                        {collectionMatches.page.total_count}
                      </strong>
                    </span>
                  )}{" "}
                  collections
                </span>
              )}
            </p>
          </Overview>
        </Header>
        {collectionMatches?._loadStatus == "rejected" && (
          <ErrorMsg
            msg={"There was a problem fetching collections"}
            showIcon={true}
          />
        )}
        <Paper>
          <CollectionsTable
            collections={collections}
            filter={filter}
            setFilter={setFilter}
          />
          {collections &&
            collections.length == 0 &&
            collectionMatches?._loadStatus == "idle" && (
              <p>There are no collections matching your criteria.</p>
            )}
          <footer className="actions">
            {collections.length > 0 &&
              collections.length !== collectionMatches?.page?.total_count && (
                <>
                  <button
                    onClick={handleLoadMore}
                    disabled={collectionMatches?._loadStatus == "loading"}
                  >
                    {collectionMatches?._loadStatus == "loading"
                      ? "Loading..."
                      : `Show ${loadableCount} more`}
                  </button>
                </>
              )}
          </footer>
        </Paper>
      </ScrollablePage>
    </Layout>
  )
}

export default CollectionsList
